import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import useScrollAnimation from '../hooks/useScrollAnimation';
import './Contact.css';

const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { delay: i * 0.15, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const contactCards = [
  {
    icon: MapPin,
    title: 'Office Address',
    highlight: false,
    content: (
      <p className="body-medium contact-detail-text">
        D-19, Connekt, 13th Floor<br />
        Gala Empire, Drive In Road<br />
        Ahmedabad, Gujarat-380052
      </p>
    ),
  },
  {
    icon: Mail,
    title: 'Email Us',
    highlight: true,
    content: (
      <>
        <a href="mailto:info@vyznit.com" className="contact-link-large">info@vyznit.com</a>
        <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '12px' }}>We'll respond within 24 hours</p>
      </>
    ),
  },
  {
    icon: Phone,
    title: 'Call Us',
    highlight: true,
    content: (
      <>
        <a href="tel:+918849173300" className="contact-link-large">+91 88491 73300</a>
        <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '12px' }}>Mon - Fri, 9:00 AM - 6:00 PM IST</p>
      </>
    ),
  },
];

const Contact = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section id="contact" className="contact-section">
      <div className="contact-container">
        <motion.div
          ref={headerRef}
          className="contact-header"
          variants={sectionVariants}
          initial="hidden"
          animate={headerVisible ? 'visible' : 'hidden'}
        >
          <div className="section-label">Contact</div>
          <h2 className="display-large">Get In <span className="gradient-text">Touch</span></h2>
          <p className="body-medium">Ready to transform your business with AI-powered solutions? Reach out to us.</p>
        </motion.div>

        <div ref={cardsRef} className="contact-content-centered">
          <div className="contact-details-grid">
            {contactCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={i}
                  className={`contact-detail-card ${card.highlight ? 'contact-detail-highlight' : ''}`}
                  custom={i}
                  variants={cardVariants}
                  initial="hidden"
                  animate={cardsVisible ? 'visible' : 'hidden'}
                  whileHover={{ y: -10, boxShadow: '0 16px 48px rgba(0, 255, 209, 0.2)' }}
                  transition={{ duration: 0.25 }}
                >
                  <motion.div
                    className="contact-icon-large"
                    whileHover={{ scale: 1.12, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  >
                    <Icon size={40} />
                  </motion.div>
                  <h3 className="heading-2">{card.title}</h3>
                  {card.content}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;